import type { MutableRefObject } from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { memoryCacheTryGet } from "@/lib/safe-query/memory-cache"
import {
  type MemoryWriteDecision,
  type RunSafeQueryOptions,
  type SafeQueryCacheMode,
  defaultSafeQueryMemoryWrite,
  invalidateQuery,
  isAbortLikeError,
  runSafeQuery,
} from "@/lib/safe-query/run-safe-query"

export type { MemoryWriteDecision, RunSafeQueryOptions, SafeQueryCacheMode }
export {
  abortActiveQueryForKey,
  defaultSafeQueryMemoryWrite,
  getSafeQueryObservabilitySnapshot,
  installSafeQueryAbortRejectionGuard,
  invalidateQuery,
  invalidateQueryPrefix,
  isAbortLikeError,
  resetSafeQueryEngineForTests,
  runSafeQuery,
} from "@/lib/safe-query/run-safe-query"

export { stableStringify } from "@/lib/safe-query/stable-stringify"
export {
  memoryCacheSize,
  SAFE_QUERY_MEMORY_MAX_ENTRIES,
  SAFE_QUERY_MEMORY_TTL_MS,
} from "@/lib/safe-query/memory-cache"

function useLatest<T>(value: T): MutableRefObject<T> {
  const r = useRef(value)
  r.current = value
  return r
}

export type UseSafeQueryOptions<T> = {
  queryKey: string
  enabled?: boolean
  fetcher: (signal: AbortSignal) => Promise<T>
  cache?: SafeQueryCacheMode
  ttlMs?: number
  memoryWrite?: (result: T) => MemoryWriteDecision
  /**
   * When the cache key changes, keep showing the previous result until the next fetch completes
   * (stale-while-revalidate style UX).
   */
  keepPreviousData?: boolean
}

export type UseSafeQueryResult<T> = {
  data: T | undefined
  error: Error | undefined
  isLoading: boolean
  refetch: (opts?: { force?: boolean }) => Promise<void>
  invalidate: () => void
}

/**
 * Declarative data loading: effect depends only on `queryKey`, `enabled`, `cache`, and optional `ttlMs`.
 * Latest `fetcher` is read via ref (do not add fetcher to effect deps).
 */
export function useSafeQuery<T>(opts: UseSafeQueryOptions<T>): UseSafeQueryResult<T> {
  const { queryKey, enabled = true, fetcher, cache = "memory", ttlMs, memoryWrite, keepPreviousData = false } = opts
  const fetcherRef = useLatest(fetcher)
  const enabledRef = useLatest(enabled)
  const memoryWriteLatest = useLatest(memoryWrite)
  const memoryWriteDispatchRef = useRef<((result: T) => MemoryWriteDecision) | null>(null)
  if (!memoryWriteDispatchRef.current) {
    memoryWriteDispatchRef.current = (result: T) => {
      const fn = memoryWriteLatest.current
      if (fn) return fn(result)
      return defaultSafeQueryMemoryWrite(result)
    }
  }

  const [data, setData] = useState<T | undefined>(() => {
    if (!queryKey || cache !== "memory") return undefined
    const hit = memoryCacheTryGet(queryKey)
    return hit.hit ? (hit.value as T) : undefined
  })
  const [error, setError] = useState<Error | undefined>()
  const [isLoading, setIsLoading] = useState(false)
  const genRef = useRef(0)

  useEffect(() => {
    if (!enabled || !queryKey) {
      setIsLoading(false)
      return
    }

    if (cache === "memory") {
      const hit = memoryCacheTryGet(queryKey)
      if (hit.hit) {
        setData(hit.value as T)
      } else if (!keepPreviousData) {
        setData(undefined)
      }
    } else if (!keepPreviousData) {
      setData(undefined)
    }

    const gen = ++genRef.current
    const ac = new AbortController()
    setIsLoading(true)
    setError(undefined)

    void runSafeQuery(queryKey, (sig) => fetcherRef.current(sig), {
      signal: ac.signal,
      cache,
      ttlMs,
      memoryWrite: memoryWriteDispatchRef.current!,
    })
      .then((d) => {
        if (gen !== genRef.current) return
        setData(d)
      })
      .catch((e) => {
        if (gen !== genRef.current) return
        if (isAbortLikeError(e) || ac.signal.aborted) {
          return
        }
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (gen !== genRef.current) return
        setIsLoading(false)
      })

    return () => {
      ac.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetcher read via ref
  }, [queryKey, enabled, cache, ttlMs, keepPreviousData])

  const refetch = useCallback(
    async (o?: { force?: boolean }) => {
      if (!queryKey) return
      if (!enabledRef.current) return
      // A key change starts a new effect generation; a refetch begun under the old key must
      // then leave data/loading/error to that newer generation instead of overwriting it.
      const gen = genRef.current
      const isCurrent = () => gen === genRef.current
      setIsLoading(true)
      setError(undefined)
      try {
        const d = await runSafeQuery(queryKey, (sig) => fetcherRef.current(sig), {
          force: Boolean(o?.force),
          cache,
          ttlMs,
          memoryWrite: memoryWriteDispatchRef.current!,
        })
        if (isCurrent()) setData(d)
      } catch (e) {
        if (isCurrent() && !isAbortLikeError(e)) {
          setError(e instanceof Error ? e : new Error(String(e)))
        }
      } finally {
        if (isCurrent()) setIsLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetcher/enabled read via ref
    [queryKey, cache, ttlMs]
  )

  const invalidate = useCallback(() => {
    invalidateQuery(queryKey)
    setData(undefined)
    setError(undefined)
  }, [queryKey])

  return { data, error, isLoading, refetch, invalidate }
}
